/**
 * MetricsGrid — сетка из 6 метрик-карточек дашборда (десктоп): активные,
 * MRR, триал, по рискам, конверсия, отменено. Данные — из сводки.
 */

import {Clock, Filter, TriangleAlert, UserMinus, UsersRound, Wallet} from 'lucide-react';

import type {SubscriptionStatus} from '@services/clients';
import type {DashboardSummary} from '@services/dashboard';
import {formatNumber} from '@utils/format';

import {fmtMln} from '../../Dashboard.utils';
import {DeltaPill} from '../../DeltaPill';
import {RisksList} from '../../RisksList';
import {Sparkline} from '../../Sparkline';
import {SplitBar} from '../../SplitBar';
import {MetricShell} from '../MetricShell';

const NUM = 'font-mono text-[33px] font-semibold -tracking-[.02em] tabular-nums';

type MetricsGridProps = {s: DashboardSummary; onPickRisk: (status: SubscriptionStatus) => void};

export const MetricsGrid = ({s, onPickRisk}: MetricsGridProps) => (
  <div className="grid grid-cols-3 gap-4">
    <MetricShell
      label="Активных клиентов"
      icon={<UsersRound className="size-4 text-primary" strokeWidth={1.9} />}
      iconClassName="bg-accent"
    >
      <div className="mt-2 flex items-baseline gap-2.5">
        <span className={NUM}>{s.active.total}</span>
        <DeltaPill pct={s.active.deltaPct} />
      </div>
      <div className="mt-auto pt-3">
        <SplitBar solo={s.active.solo} clinic={s.active.clinic} />
      </div>
    </MetricShell>

    <MetricShell
      label="MRR · месячная выручка"
      icon={<Wallet className="size-4 text-success" strokeWidth={1.9} />}
      iconClassName="bg-[#E3F5F1]"
    >
      <div className="mt-2 flex items-baseline gap-2.5">
        <span className={NUM}>
          {fmtMln(s.mrr.value)}
          <span className="text-[20px] text-muted-foreground">М</span>
        </span>
        <DeltaPill pct={s.mrr.deltaPct} />
      </div>
      <div className="mt-1.5 text-[12.5px] text-muted-foreground">
        сом · +{formatNumber(s.mrr.deltaAbs)} к прошлому месяцу
      </div>
      <div className="mt-auto pt-2">
        <Sparkline points={s.mrr.spark} />
      </div>
    </MetricShell>

    <MetricShell
      label="В триале сейчас"
      icon={<Clock className="size-4 text-skr-warn" strokeWidth={1.9} />}
      iconClassName="bg-[#FBF2E1]"
    >
      <div className={`mt-2 ${NUM}`}>{s.trial.total}</div>
      <div className="mt-auto flex gap-2 pt-3">
        <TrialBox value={s.trial.noCard} label="без карты" warn />
        <TrialBox value={s.trial.withCard} label="карта есть" />
      </div>
    </MetricShell>

    <MetricShell
      label={
        <span className="flex items-center gap-[7px] font-medium text-muted-foreground">
          <TriangleAlert className="size-3.5 text-skr-danger" strokeWidth={2} />
          По рискам
        </span>
      }
    >
      <RisksList risks={s.risks} onPick={onPickRisk} />
    </MetricShell>

    <MetricShell
      label="Конверсия из заявок"
      icon={<Filter className="size-4 text-primary" strokeWidth={1.9} />}
      iconClassName="bg-accent"
    >
      <div className="mt-2 flex items-baseline">
        <span className={NUM}>
          {s.conversion.pct}
          <span className="text-[20px] text-muted-foreground">%</span>
        </span>
      </div>
      <div className="mt-1.5 text-[12.5px] text-muted-foreground">
        {s.conversion.converted} клиента из {s.conversion.leads} заявок
      </div>
      <div className="mt-auto h-2 overflow-hidden rounded-[5px] bg-skr-line2">
        <div className="h-full rounded-[5px] bg-primary" style={{width: `${s.conversion.pct}%`}} />
      </div>
    </MetricShell>

    <MetricShell
      label="Отменено за месяц"
      icon={<UserMinus className="size-4 text-[#55524C]" strokeWidth={1.9} />}
      iconClassName="bg-[#F0EEEC]"
    >
      <div className="mt-2 flex items-baseline gap-2.5">
        <span className={NUM}>{s.cancelled.total}</span>
        <DeltaPill pct={s.cancelled.deltaPct} negative />
      </div>
      <div className="mt-auto text-[12.5px] text-muted-foreground">
        churn · {s.cancelled.solo} соло · {s.cancelled.clinic} клиники
      </div>
    </MetricShell>
  </div>
);

const TrialBox = ({value, label, warn}: {value: number; label: string; warn?: boolean}) => (
  <div
    className={`flex-1 rounded-[9px] border px-[11px] py-[9px] ${
      warn ? 'border-[#F2E5CA] bg-[#FBF3E4]' : 'border-skr-line bg-[#F4F5F7]'
    }`}
  >
    <div className={`font-mono text-lg font-semibold ${warn ? 'text-skr-warn' : 'text-skr-ink'}`}>
      {value}
    </div>
    <div className={`mt-px text-[11px] ${warn ? 'text-[#9A7B36]' : 'text-muted-foreground'}`}>
      {label}
    </div>
  </div>
);
